/** Mind's consumer of viewport vertexLayer / setVertexData.
 * Caller owns ImageData, camera permission/tracks, scheduling and scene lifetime.
 * No media acquisition, network, storage, DOM or animation loop in this module.
 */
import { sampleRetinotopy } from './sampler.mjs';

export const RETINOTOPY_VERTICES = 20484; // fsaverage5: LH 10242, then RH 10242.

export function supportsLiveRetinotopy(viewport) {
  return typeof viewport?.send === 'function' && typeof viewport?.setVertexData === 'function';
}

/** Bind one layer. uv/valid are the immutable Benson vision v3 lookup arrays.
 * The returned push() uses immediate, untimestamped frames: no setTime coupling.
 * Use wantsFrame(nowMs) before an expensive canvas readback; push checks it too.
 * force bypasses the frame cap for a newly selected still image.
 * look, for the luminance channel only, is the engine vertexLayer look the scalar layer draws with
 * (colormap, range, blend, albedo, emission, rise…); without one, the full gray range at constant opacity.
 */
export async function createRetinotopyLayer(viewport, {
  uv, valid, id = 'mind:vision', channel = 'rgb', maxHz = 30, look = null,
} = {}) {
  if (!supportsLiveRetinotopy(viewport)) throw new Error('Viewport does not support live vertex layers');
  if (!(uv instanceof Float32Array) || uv.length !== RETINOTOPY_VERTICES * 2 ||
      !(valid instanceof Uint8Array) || valid.length !== RETINOTOPY_VERTICES) {
    throw new Error('Expected fsaverage5 UV f32 [20484,2] and validity u8 [20484]');
  }
  if (!['rgb', 'luminance'].includes(channel)) throw new Error('Unknown sample channel');
  if (!Number.isFinite(maxHz) || maxHz <= 0 || maxHz > 60) throw new Error('maxHz must be in (0,60]');
  if (typeof id !== 'string' || !id.length) throw new Error('A vertex layer id is required');
  if (look !== null && (typeof look !== 'object' || Array.isArray(look))) throw new Error('look must be an object');
  if (look && channel === 'rgb') throw new Error('An RGB layer carries its own colours; look applies to luminance');

  // Snapshot the reference lookup once, and exclude invalid coordinates as well
  // as invalid areas. Otherwise a bad UV would appear as valid black in scalar mode.
  const coordinates = new Float32Array(uv), mask = new Uint8Array(valid);
  let validVertices = 0;
  for (let i = 0; i < mask.length; i++) {
    const u = coordinates[2 * i], v = coordinates[2 * i + 1];
    mask[i] = Number(Boolean(mask[i]) && Number.isFinite(u) && Number.isFinite(v) &&
      u >= 0 && u <= 1 && v >= 0 && v <= 1);
    validVertices += mask[i];
  }
  const rgba = channel === 'rgb';
  const sampled = new Float32Array(RETINOTOPY_VERTICES * (rgba ? 4 : 1));
  const payload = rgba ? new Uint8Array(sampled.length) : sampled;
  const command = { cmd: 'vertexLayer', id, surface: 'cortex', space: 'fsaverage5',
    kind: rgba ? 'rgba' : 'scalar' };
  // The field default fades zero into tissue. Image brightness needs the full
  // gray range at constant opacity; NaN alone supplies the cortical mask.
  if (!rgba) command.look = look ?? { colormap: 'gray', range: [0, 1],
    blend: 'mix', albedo: 1, rise: [-1, 1] };
  const binding = await viewport.send(command);
  if (binding?.vertices !== RETINOTOPY_VERTICES) {
    await viewport.send({ cmd: 'vertexLayer', id, remove: true });
    throw new Error(`Viewport returned ${binding?.vertices} vertices; expected ${RETINOTOPY_VERTICES}`);
  }

  let lastPushMs = -Infinity, disposed = false, removal;
  const intervalMs = 1000 / maxHz;
  function checkClock(nowMs) {
    if (!Number.isFinite(nowMs)) throw new Error('Frame time must be finite monotonic milliseconds');
    if (nowMs < lastPushMs) throw new Error('Frame time moved backwards; use the same monotonic clock');
  }
  function wantsFrame(nowMs = performance.now()) {
    if (disposed) return false;
    checkClock(nowMs);
    return nowMs - lastPushMs >= intervalMs;
  }
  return Object.freeze({
    id, channel, validVertices, bytesPerPush: payload.byteLength,
    binding: Object.freeze({ ...binding }),
    wantsFrame,
    push(image, { nowMs = performance.now(), mirror = false, force = false } = {}) {
      if (disposed) return false;
      checkClock(nowMs);
      if (!force && !wantsFrame(nowMs)) return false;
      sampleRetinotopy(image, coordinates, mask, { channel, mirror, out: sampled });
      if (rgba) {
        // Display sRGB, straight alpha. Alpha marks cortical coverage; the sampler
        // has already composited transparent source pixels over black.
        for (let i = 0; i < payload.length; i++) {
          payload[i] = Math.max(0, Math.min(255, Math.round(sampled[i] * 255)));
        }
      } else {
        // Zero is valid black. NaN alone means "keep the tissue colour".
        for (let i = 0; i < mask.length; i++) if (!mask[i]) payload[i] = NaN;
      }
      // Contract: copies synchronously into staging. Safe to reuse both arrays.
      // Omit t deliberately; an app's demo clock must not delay a live frame.
      viewport.setVertexData(id, payload);
      lastPushMs = nowMs;
      return true;
    },
    dispose() {
      // Stop pushes synchronously, even while the remove command is pending.
      if (!disposed) {
        disposed = true;
        removal = Promise.resolve().then(() => viewport.send({ cmd: 'vertexLayer', id, remove: true }));
      }
      return removal;
    },
  });
}
